/**
 * Connector API Client (AI Extension)
 * Handles all API calls for Connector entities
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import {
  Connector,
  StandardListRequest,
  GetByIdRequest,
  SearchRequest,
  TestConnectorConnectionRequest,
  TestConnectorConnectionResponseData,
} from '../types';

export class ConnectorApiClient extends BaseApiClient {
  async getAll(request: StandardListRequest = {}): Promise<ApiResponse<Connector[]>> {
    return this.post<Connector[]>(API_ENDPOINTS.CONNECTORS.LIST, request);
  }

  async getById(request: GetByIdRequest): Promise<ApiResponse<Connector>> {
    // Backend expects GetByIdWebRequest format: { ID: { ID: number } }
    // Use postQuery to spread properties at root level alongside metadata
    const webRequest = {
      ID: {
        ID: request.id,
      },
    };
    return this.postQuery<Connector>(API_ENDPOINTS.CONNECTORS.GET_BY_ID, webRequest);
  }

  async getActive(request: SearchRequest = {}): Promise<ApiResponse<Connector[]>> {
    return this.post<Connector[]>(API_ENDPOINTS.CONNECTORS.ACTIVE, request);
  }

  async update(connector: Connector): Promise<ApiResponse<Connector>> {
    const id = connector.ConnectorID;
    return this.put<Connector>(API_ENDPOINTS.CONNECTORS.UPDATE(id), connector);
  }

  /**
   * Test connector connection to external service
   * Phase 1: API Components & Basic UI
   */
  async testConnection(
    request: TestConnectorConnectionRequest
  ): Promise<ApiResponse<TestConnectorConnectionResponseData>> {
    const connectorId = typeof request.ConnectorID === 'number' ? request.ConnectorID : request.ConnectorID.ID;
    // Backend expects request with ConnectorID
    const webRequest = {
      ConnectorID: connectorId,
    };
    return this.postQuery<TestConnectorConnectionResponseData>(
      API_ENDPOINTS.CONNECTORS.TEST_CONNECTION(connectorId),
      webRequest
    );
  }
}

export const connectorApiClient = new ConnectorApiClient();
